import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';

import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import {
  WorkOrderStatusMilestone,
  WorkOrderV2Row,
  WorkOrderV2StatusPayload,
  WorkOrderV2StatusTimelineInfo,
} from './work-orders-v2.models';
import { WorkOrdersV2Service } from './work-orders-v2.service';

export interface WorkOrderStatusFlowDialogData {
  row: WorkOrderV2Row;
  milestones: WorkOrderStatusMilestone[];
  selectedStatus?: string | null;
  themeMode?: 'dark' | 'light';
}

export interface WorkOrderStatusFlowDialogResult {
  changed: boolean;
}

interface WorkOrderStatusFlowItem {
  milestone: WorkOrderStatusMilestone;
  event: WorkOrderV2StatusTimelineInfo | null;
}

interface WorkOrderStatusEditState {
  event: WorkOrderV2StatusTimelineInfo;
  eventDate: string;
  eventTime: string;
  eventTimezone: string;
  overrideLocalTime: boolean;
  localDate: string;
  localTime: string;
  note: string;
}

@Component({
  selector: 'app-work-order-status-flow-dialog',
  template: `
    <div
      class="wosf-dialog flex max-h-[84vh] w-[min(920px,94vw)] flex-col overflow-hidden rounded-2xl border border-[var(--wosf-border)] bg-[var(--wosf-bg)] text-[var(--wosf-text)] shadow-2xl"
      [attr.data-theme]="themeMode"
    >
      <header class="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--wosf-border)] bg-[var(--wosf-surface)] px-4 py-3">
        <div class="flex min-w-0 items-center gap-3">
          <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-500/10 text-sky-400">
            <i class="fa-solid fa-route text-sm"></i>
          </span>
          <div class="min-w-0">
            <div class="flex min-w-0 items-baseline gap-2">
              <h2 class="m-0 truncate text-base font-black leading-tight">Status milestones</h2>
              <span class="truncate text-xs font-bold text-[var(--wosf-muted)]">{{ orderLabel }}</span>
            </div>
            <p class="m-0 mt-0.5 text-[10px] font-bold text-[var(--wosf-soft)]">
              Event time · consignee local · <i class="fa-solid fa-lock mr-0.5 text-[9px]"></i>server recorded
            </p>
          </div>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <span class="max-w-56 truncate rounded-full border border-sky-400/30 bg-sky-500/10 px-2.5 py-1 text-[10px] font-black uppercase text-[var(--wosf-accent-text)]">
            {{ currentStatus }}
          </span>
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--wosf-border)] bg-[var(--wosf-surface-2)] text-[var(--wosf-muted)] transition hover:border-sky-400 hover:text-sky-300"
            (click)="close()"
            aria-label="Close"
            title="Close"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </header>

      <main class="wosf-scroll min-h-0 flex-1 overflow-auto px-3 py-2.5">
        <div *ngIf="loading" class="flex min-h-48 items-center justify-center gap-2 text-xs font-bold text-[var(--wosf-muted)]">
          <i class="fa-solid fa-spinner fa-spin"></i>
          Loading status history&hellip;
        </div>

        <div *ngIf="!loading && errorMessage" class="flex min-h-48 items-center justify-center gap-2 text-xs font-bold text-rose-400">
          <i class="fa-solid fa-triangle-exclamation"></i>
          {{ errorMessage }}
        </div>

        <div *ngIf="!loading && !errorMessage" class="space-y-1.5">
          <div class="hidden grid-cols-[1.5rem_minmax(150px,1.1fr)_minmax(155px,1fr)_minmax(155px,1fr)_minmax(140px,.9fr)_2rem] items-center gap-x-3 px-2 text-[9px] font-black uppercase tracking-wide text-[var(--wosf-soft)] md:grid">
            <span></span>
            <span>Milestone</span>
            <span>Event time</span>
            <span>Consignee local</span>
            <span><i class="fa-solid fa-lock mr-1 text-[8px]"></i>Recorded</span>
            <span></span>
          </div>

          <section
            *ngFor="let item of items; let index = index"
            class="rounded-lg border border-[var(--wosf-border)] bg-[var(--wosf-surface)] px-2 py-1.5 transition"
            [class.wosf-recorded]="!!item.event"
            [class.wosf-selected]="data.selectedStatus === item.milestone.status"
          >
            <div class="grid grid-cols-[1.5rem_minmax(0,1fr)_2rem] items-center gap-x-3 md:grid-cols-[1.5rem_minmax(150px,1.1fr)_minmax(155px,1fr)_minmax(155px,1fr)_minmax(140px,.9fr)_2rem]">
              <span class="wosf-step inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--wosf-step)] text-[10px] font-black text-[var(--wosf-step-text)]">
                {{ index + 1 }}
              </span>

              <div class="min-w-0 py-0.5">
                <strong class="block truncate text-xs font-black leading-tight">{{ item.milestone.label }}</strong>
                <span class="mt-0.5 block truncate text-[10px] font-bold text-[var(--wosf-muted)]">{{ item.milestone.status }}</span>
              </div>

              <ng-container *ngIf="item.event as event; else missingEvent">
                <div class="hidden min-w-0 md:block">
                  <strong class="block truncate text-[11px] font-extrabold">{{ formatEventTime(event) }}</strong>
                  <span class="mt-0.5 block truncate text-[9px] font-bold text-[var(--wosf-soft)]">{{ browserTimezone }}</span>
                </div>
                <div class="hidden min-w-0 md:block">
                  <strong class="block truncate text-[11px] font-extrabold">{{ formatConsigneeTime(event) }}</strong>
                  <span class="mt-0.5 block truncate text-[9px] font-bold text-[var(--wosf-soft)]">{{ event.time_zone_id || 'Timezone unavailable' }}</span>
                </div>
                <div class="hidden min-w-0 md:block">
                  <strong class="block truncate text-[11px] font-extrabold">{{ formatRecordedTime(event.created) }}</strong>
                  <span class="mt-0.5 block truncate text-[9px] font-bold text-[var(--wosf-soft)]">Server generated · read only</span>
                </div>

                <button
                  type="button"
                  class="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[var(--wosf-border-strong)] bg-[var(--wosf-surface-2)] text-[10px] text-[var(--wosf-muted)] transition hover:border-sky-400 hover:text-sky-300"
                  [disabled]="saving"
                  (click)="startEdit(event)"
                  [attr.aria-label]="'Edit ' + item.milestone.label"
                  [title]="'Edit ' + item.milestone.label"
                >
                  <i class="fa-solid fa-pen"></i>
                </button>
              </ng-container>

              <ng-template #missingEvent>
                <span class="hidden truncate text-[10px] font-bold text-[var(--wosf-soft)] md:col-span-3 md:block">Not recorded</span>
                <span class="inline-flex h-7 w-7 items-center justify-center text-[10px] text-[var(--wosf-soft)]" title="Not recorded">
                  <i class="fa-regular fa-circle"></i>
                </span>
              </ng-template>
            </div>

            <div *ngIf="item.event as event" class="md:hidden">
              <div class="ml-9 mt-1 grid grid-cols-2 gap-1 text-[10px]">
                <span class="truncate text-[var(--wosf-muted)]">Event: {{ formatEventTime(event) }}</span>
                <span class="truncate text-[var(--wosf-muted)]">Local: {{ formatConsigneeTime(event) }}</span>
              </div>
            </div>

            <p
              *ngIf="item.event?.lading_status_note && editing?.event?.lading_status_timeline_uuid !== item.event?.lading_status_timeline_uuid"
              class="m-0 ml-9 mt-1 truncate text-[10px] font-semibold italic text-[var(--wosf-muted)]"
              [title]="item.event?.lading_status_note || ''"
            >
              {{ item.event?.lading_status_note }}
            </p>

            <form
              *ngIf="item.event && editing?.event?.lading_status_timeline_uuid === item.event.lading_status_timeline_uuid"
              class="ml-9 mt-2 border-t border-[var(--wosf-border)] pt-2"
              (ngSubmit)="prepareSave()"
            >
              <div class="grid gap-2 md:grid-cols-[1fr_1fr_1.35fr]">
                <label class="min-w-0">
                  <span class="block text-[9px] font-black uppercase tracking-wide text-[var(--wosf-soft)]">Event date</span>
                  <input class="mt-1 h-8 w-full rounded-md border border-[var(--wosf-border-strong)] bg-[var(--wosf-input)] px-2 text-xs font-bold text-[var(--wosf-text)] outline-none focus:border-sky-400" type="date" [(ngModel)]="editing!.eventDate" name="eventDate" required>
                </label>
                <label class="min-w-0">
                  <span class="block text-[9px] font-black uppercase tracking-wide text-[var(--wosf-soft)]">Event time</span>
                  <input class="mt-1 h-8 w-full rounded-md border border-[var(--wosf-border-strong)] bg-[var(--wosf-input)] px-2 text-xs font-bold text-[var(--wosf-text)] outline-none focus:border-sky-400" type="time" [(ngModel)]="editing!.eventTime" name="eventTime" required>
                </label>
                <label class="min-w-0">
                  <span class="block text-[9px] font-black uppercase tracking-wide text-[var(--wosf-soft)]">Event timezone</span>
                  <input class="mt-1 h-8 w-full rounded-md border border-[var(--wosf-border-strong)] bg-[var(--wosf-input)] px-2 text-xs font-bold text-[var(--wosf-text)] outline-none focus:border-sky-400" type="text" [(ngModel)]="editing!.eventTimezone" name="eventTimezone" required>
                </label>
              </div>

              <label class="mt-2 flex cursor-pointer items-start gap-2 rounded-md border border-[var(--wosf-border)] bg-[var(--wosf-surface-2)] px-2 py-1.5">
                <input class="mt-0.5 h-3.5 w-3.5 shrink-0 accent-sky-500" type="checkbox" [(ngModel)]="editing!.overrideLocalTime" name="overrideLocalTime">
                <span class="min-w-0">
                  <strong class="block text-[10px] font-black">Override consignee-local time</strong>
                  <small class="block text-[9px] font-semibold text-[var(--wosf-muted)]">Off = backend calculates the consignee time and timezone.</small>
                </span>
              </label>

              <div *ngIf="editing!.overrideLocalTime" class="mt-2 grid gap-2 md:grid-cols-2">
                <label>
                  <span class="block text-[9px] font-black uppercase tracking-wide text-[var(--wosf-soft)]">Consignee-local date</span>
                  <input class="mt-1 h-8 w-full rounded-md border border-[var(--wosf-border-strong)] bg-[var(--wosf-input)] px-2 text-xs font-bold text-[var(--wosf-text)] outline-none focus:border-sky-400" type="date" [(ngModel)]="editing!.localDate" name="localDate" required>
                </label>
                <label>
                  <span class="block text-[9px] font-black uppercase tracking-wide text-[var(--wosf-soft)]">Consignee-local time</span>
                  <input class="mt-1 h-8 w-full rounded-md border border-[var(--wosf-border-strong)] bg-[var(--wosf-input)] px-2 text-xs font-bold text-[var(--wosf-text)] outline-none focus:border-sky-400" type="time" [(ngModel)]="editing!.localTime" name="localTime" required>
                </label>
              </div>

              <div class="mt-2 flex items-end gap-2">
                <label class="min-w-0 flex-1">
                  <span class="block text-[9px] font-black uppercase tracking-wide text-[var(--wosf-soft)]">Status note</span>
                  <input class="mt-1 h-8 w-full rounded-md border border-[var(--wosf-border-strong)] bg-[var(--wosf-input)] px-2 text-xs font-semibold text-[var(--wosf-text)] outline-none focus:border-sky-400" [(ngModel)]="editing!.note" name="note">
                </label>
                <button type="button" class="inline-flex h-8 items-center justify-center rounded-md border border-[var(--wosf-border-strong)] bg-[var(--wosf-surface-2)] px-3 text-[10px] font-black text-[var(--wosf-muted)] hover:border-slate-500" [disabled]="saving" (click)="cancelEdit()">Cancel</button>
                <button type="submit" class="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-sky-500 bg-sky-600 px-3 text-[10px] font-black text-white hover:bg-sky-500 disabled:cursor-wait disabled:opacity-50" [disabled]="saving || !isEditValid">
                  <i *ngIf="saving" class="fa-solid fa-spinner fa-spin"></i>
                  {{ saving ? 'Saving' : 'Save' }}
                </button>
              </div>
            </form>
          </section>
        </div>
      </main>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .wosf-dialog {
      --wosf-bg: #050b16;
      --wosf-surface: #0c1525;
      --wosf-surface-2: #111d30;
      --wosf-input: #07101f;
      --wosf-border: #22324b;
      --wosf-border-strong: #365071;
      --wosf-text: #eef5ff;
      --wosf-muted: #8ea3bf;
      --wosf-soft: #647994;
      --wosf-step: #17243a;
      --wosf-step-text: #8ea3bf;
      --wosf-accent-text: #7dd3fc;
      color-scheme: dark;
    }
    .wosf-dialog[data-theme='light'] {
      --wosf-bg: #f6f9fc;
      --wosf-surface: #ffffff;
      --wosf-surface-2: #edf3f8;
      --wosf-input: #ffffff;
      --wosf-border: #d4deea;
      --wosf-border-strong: #b5c6d9;
      --wosf-text: #0c1728;
      --wosf-muted: #5c6f86;
      --wosf-soft: #7b8da3;
      --wosf-step: #e3ebf4;
      --wosf-step-text: #5c6f86;
      --wosf-accent-text: #0369a1;
      color-scheme: light;
    }
    .wosf-recorded { border-color: rgba(16, 185, 129, .38) !important; background: rgba(16, 185, 129, .07) !important; }
    .wosf-recorded .wosf-step { background: #10b981 !important; color: #fff !important; }
    .wosf-selected { box-shadow: inset 3px 0 0 #38bdf8, 0 0 0 1px rgba(56, 189, 248, .18); }
    .wosf-scroll { scrollbar-color: var(--wosf-border-strong) var(--wosf-bg); scrollbar-width: thin; }
    button:disabled { cursor: wait; opacity: .55; }
  `],
})
export class WorkOrderStatusFlowDialogComponent implements OnInit {
  items: WorkOrderStatusFlowItem[] = [];
  editing: WorkOrderStatusEditState | null = null;
  loading = true;
  saving = false;
  changed = false;
  errorMessage = '';
  readonly browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  readonly themeMode: 'dark' | 'light';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: WorkOrderStatusFlowDialogData,
    private readonly dialogRef: MatDialogRef<WorkOrderStatusFlowDialogComponent, WorkOrderStatusFlowDialogResult>,
    private readonly dialog: MatDialog,
    private readonly workOrders: WorkOrdersV2Service,
    private readonly toastr: ToastrService,
  ) {
    this.themeMode = data?.themeMode === 'light' ? 'light' : 'dark';
  }

  ngOnInit(): void {
    this.loadTimeline();
  }

  get orderLabel(): string {
    return String(this.data.row.order_label || this.data.row.lading_uuid);
  }

  get currentStatus(): string {
    return String(this.data.row.lading_status || this.data.row.status_label || 'Status not set');
  }

  get isEditValid(): boolean {
    if (!this.editing) return false;
    if (!this.editing.eventDate || !this.editing.eventTime || !this.editing.eventTimezone.trim()) return false;
    return !this.editing.overrideLocalTime || (!!this.editing.localDate && !!this.editing.localTime);
  }

  startEdit(event: WorkOrderV2StatusTimelineInfo): void {
    const eventTime = this.canonicalEvent(event)?.setZone(this.browserTimezone);
    this.editing = {
      event,
      eventDate: eventTime?.toFormat('yyyy-MM-dd') || '',
      eventTime: eventTime?.toFormat('HH:mm') || '',
      eventTimezone: this.browserTimezone,
      overrideLocalTime: false,
      localDate: event.local_time?.slice(0, 10) || '',
      localTime: event.local_time?.slice(11, 16) || '',
      note: event.lading_status_note || '',
    };
  }

  cancelEdit(): void {
    this.editing = null;
  }

  prepareSave(): void {
    if (!this.editing || !this.isEditValid || this.saving) return;
    if (this.editing.event.lading_status !== 'Delivered Consignee') {
      this.persistEdit(false);
      return;
    }

    this.saving = true;
    this.workOrders.getProductDeliveryPreview(this.data.row.lading_uuid).pipe(take(1)).subscribe({
      next: response => {
        this.saving = false;
        if (!this.responseSucceeded(response)) {
          this.toastr.error(response.message || 'Could not check product delivery timestamps.');
          return;
        }

        const missingCount = this.count(response.missing_delivery_count);
        if (missingCount === 0) {
          this.persistEdit(false);
          return;
        }

        this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: 'Mark products delivered?',
            message: `This lading has ${missingCount} ${missingCount === 1 ? 'product' : 'products'} without a delivery time.\n\nApply this Delivered Consignee event time to ${missingCount === 1 ? 'it' : 'them'}? Existing product delivery times will not be changed.`,
            confirmText: 'Mark delivered',
            cancelText: 'Status only',
            tone: 'warning',
          },
        }).afterClosed().pipe(take(1)).subscribe(confirmed => this.persistEdit(confirmed === true));
      },
      error: () => {
        this.saving = false;
        this.toastr.error('Could not check product delivery timestamps.');
      },
    });
  }

  formatEventTime(event: WorkOrderV2StatusTimelineInfo): string {
    const value = this.canonicalEvent(event)?.setZone(this.browserTimezone);
    return value?.isValid ? value.toFormat('MMM d, yyyy · h:mm a') : 'Not supplied';
  }

  formatConsigneeTime(event: WorkOrderV2StatusTimelineInfo): string {
    if (!event.local_time) return 'Not resolved';
    const parsed = DateTime.fromSQL(event.local_time, { zone: event.time_zone_id || 'UTC' });
    return parsed.isValid ? parsed.toFormat('MMM d, yyyy · h:mm a') : event.local_time;
  }

  formatRecordedTime(value: string | null | undefined): string {
    if (!value) return 'Pending server timestamp';
    const parsed = DateTime.fromSQL(value, { zone: 'UTC' });
    return parsed.isValid ? parsed.toFormat('MMM d, yyyy · h:mm a') : value;
  }

  close(): void {
    this.dialogRef.close({ changed: this.changed });
  }

  private loadTimeline(): void {
    this.loading = true;
    this.errorMessage = '';
    this.workOrders.loadStatusTimeline(this.data.row.lading_uuid).pipe(take(1)).subscribe({
      next: response => {
        this.loading = false;
        if (!this.responseSucceeded(response)) {
          this.errorMessage = response.message || 'Could not load the status timeline.';
          return;
        }
        const events = Array.isArray(response.data) ? response.data : [];
        this.items = this.data.milestones.map(milestone => ({
          milestone,
          event: events.find(event => event.lading_status === milestone.status) || null,
        }));
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Could not load the status timeline.';
      },
    });
  }

  private persistEdit(markProductsDelivered: boolean): void {
    if (!this.editing || !this.isEditValid) return;
    const edit = this.editing;
    const payload: WorkOrderV2StatusPayload = {
      lading_uuid: this.data.row.lading_uuid,
      lading_status: edit.event.lading_status,
      lading_status_timeline_uuid: edit.event.lading_status_timeline_uuid,
      event_date: edit.eventDate,
      event_time: edit.eventTime,
      event_timezone: edit.eventTimezone.trim(),
      override_local_time: edit.overrideLocalTime,
      consignee_local_date: edit.overrideLocalTime ? edit.localDate : undefined,
      consignee_local_time: edit.overrideLocalTime ? edit.localTime : undefined,
      lading_status_note: edit.note.trim() || null,
    };

    this.saving = true;
    this.workOrders.updateStatus(payload).pipe(take(1)).subscribe({
      next: response => {
        if (!this.responseSucceeded(response)) {
          this.saving = false;
          this.toastr.error(response.message || 'Could not save the status event.');
          return;
        }

        this.changed = true;
        this.toastr.success(response.message || 'Status event saved.');
        if (!markProductsDelivered) {
          this.finishSave();
          return;
        }

        const deliveredTimeUtc = this.deliveryTimeUtc(response.data);
        if (!deliveredTimeUtc) {
          this.toastr.error('The status was saved, but its delivery time could not be applied to products.');
          this.finishSave();
          return;
        }

        this.workOrders.markProductsDelivered(this.data.row.lading_uuid, deliveredTimeUtc).pipe(take(1)).subscribe({
          next: productResponse => {
            if (this.responseSucceeded(productResponse)) {
              const updated = this.count(productResponse.updated_count);
              this.toastr.success(`${updated} ${updated === 1 ? 'product was' : 'products were'} marked delivered.`);
            } else {
              this.toastr.error(productResponse.message || 'The status was saved, but the products were not updated.');
            }
            this.finishSave();
          },
          error: () => {
            this.toastr.error('The status was saved, but the products were not updated.');
            this.finishSave();
          },
        });
      },
      error: () => {
        this.saving = false;
        this.toastr.error('Could not save the status event.');
      },
    });
  }

  private finishSave(): void {
    this.saving = false;
    this.editing = null;
    this.loadTimeline();
  }

  private canonicalEvent(event: WorkOrderV2StatusTimelineInfo): DateTime | null {
    if (!event.lading_status_updated) return null;
    const parsed = DateTime.fromSQL(event.lading_status_updated, { zone: 'America/New_York' });
    return parsed.isValid ? parsed : null;
  }

  private deliveryTimeUtc(event: WorkOrderV2StatusTimelineInfo | undefined): string | null {
    if (!event?.local_time || !event.time_zone_id) return null;
    const parsed = DateTime.fromSQL(event.local_time, { zone: event.time_zone_id });
    return parsed.isValid ? parsed.toUTC().toFormat('yyyy-MM-dd HH:mm:ss') : null;
  }

  private responseSucceeded(response: { success?: boolean; code?: string | number } | null | undefined): boolean {
    return response?.success === true || String(response?.code) === '200';
  }

  private count(value: string | number | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
  }
}
